<template>
  <!-- Breadcrumb mode -->
  <header v-if="breadcrumb" class="kds-topbar kds-topbar--breadcrumb" role="banner">
    <nav class="kds-topbar__breadcrumb" aria-label="Breadcrumb">
      <ol class="kds-topbar__breadcrumb-list">
        <li class="kds-topbar__breadcrumb-item">{{ breadcrumb.workspace }}</li>
        <li class="kds-topbar__breadcrumb-sep" aria-hidden="true">/</li>
        <li class="kds-topbar__breadcrumb-item kds-topbar__breadcrumb-item--view">{{ breadcrumb.view }}</li>
      </ol>
    </nav>
    <div class="kds-topbar__actions">
      <button
        v-if="ctaSecondaryVisible"
        type="button"
        class="kds-topbar__cta kds-topbar__cta--secondary"
        :class="{ 'kds-topbar__cta--disabled': ctaSecondaryDisabled }"
        :disabled="ctaSecondaryDisabled"
        :aria-disabled="ctaSecondaryDisabled ? 'true' : undefined"
        :title="ctaSecondaryDisabled ? ctaSecondaryDisabledTitle : undefined"
        @click="onSecondaryClick"
      >
        <KdsIcon :name="ctaSecondaryIcon || 'file-input'" :size="18" />
        <span>{{ ctaSecondaryLabel }}</span>
      </button>
      <button
        v-if="ctaPrimaryVisible"
        type="button"
        class="kds-topbar__cta kds-topbar__cta--primary"
        :class="{ 'kds-topbar__cta--disabled': ctaPrimaryDisabled }"
        :disabled="ctaPrimaryDisabled"
        :aria-disabled="ctaPrimaryDisabled ? 'true' : undefined"
        :title="ctaPrimaryDisabled ? ctaPrimaryDisabledTitle : undefined"
        @click="onPrimaryClick"
      >
        <KdsIcon :name="ctaPrimaryIcon || 'plus'" :size="18" />
        <span>{{ ctaPrimaryLabel }}</span>
      </button>
    </div>
  </header>

  <!-- Legacy mode -->
  <header v-else class="kds-topbar">
    <div class="kds-topbar__brand">
      <span class="kds-topbar__brand-mark" aria-hidden="true">
        <svg viewBox="0 0 64 64" width="22" height="22" fill="none">
          <circle cx="32" cy="32" r="22" stroke="currentColor" stroke-width="3.5" fill="none" />
        </svg>
      </span>
      <span class="kds-topbar__brand-name">{{ brand }}</span>
    </div>
    <span v-if="title" class="kds-topbar__title">{{ title }}</span>
    <div v-if="protocolLabel" class="kds-topbar__protocol">
      <KdsEyebrow>Protocol</KdsEyebrow>
      <span class="kds-topbar__protocol-name">{{ protocolLabel }}</span>
    </div>
    <div class="kds-topbar__spacer" />
    <KdsLiveIndicator v-if="live" :turn="turn" :total="total" />
  </header>
</template>

<script setup lang="ts">
import KdsEyebrow from '@/kds/primitives/KdsEyebrow.vue';
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import KdsLiveIndicator from './KdsLiveIndicator.vue';
import type { Breadcrumb, KdsIconName } from '@/kds/types';

interface Props {
  // Legacy props
  brand?: string;
  title?: string;
  protocolLabel?: string;
  live?: boolean;
  turn?: number;
  total?: number;
  // Breadcrumb mode props
  breadcrumb?: Breadcrumb;
  ctaPrimaryLabel?: string;
  ctaPrimaryIcon?: KdsIconName;
  ctaSecondaryLabel?: string;
  ctaSecondaryIcon?: KdsIconName;
  ctaSecondaryDisabled?: boolean;
  ctaSecondaryDisabledTitle?: string;
  ctaPrimaryDisabled?: boolean;
  ctaPrimaryDisabledTitle?: string;
  /** Opt-in visibility — defaults to false so CTAs are hidden unless explicitly enabled (D1, CA-1). */
  ctaPrimaryVisible?: boolean;
  /** Opt-in visibility — defaults to false so CTAs are hidden unless explicitly enabled (D1, CA-1). */
  ctaSecondaryVisible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  brand: undefined,
  title: undefined,
  protocolLabel: undefined,
  live: false,
  turn: 1,
  total: 1,
  breadcrumb: undefined,
  ctaPrimaryLabel: 'New panel',
  ctaPrimaryIcon: 'plus',
  ctaSecondaryLabel: 'Import sources',
  ctaSecondaryIcon: 'file-input',
  ctaSecondaryDisabled: false,
  ctaSecondaryDisabledTitle: undefined,
  ctaPrimaryDisabled: false,
  ctaPrimaryDisabledTitle: undefined,
  ctaPrimaryVisible: false,
  ctaSecondaryVisible: false
});

const emit = defineEmits<{
  (e: 'import-sources'): void;
  (e: 'new-panel'): void;
}>();

function onSecondaryClick() {
  if (props.ctaSecondaryDisabled) return;
  emit('import-sources');
}

function onPrimaryClick() {
  if (props.ctaPrimaryDisabled) return;
  emit('new-panel');
}
</script>

<style scoped lang="scss">
.kds-topbar {
  display: flex;
  align-items: center;
  gap: var(--space-5);
  padding: var(--space-3) var(--space-6);
  border-bottom: var(--border);
  backdrop-filter: blur(16px);
  z-index: 3;

  // -----------------------------------------------
  // Legacy mode
  // -----------------------------------------------
  &__brand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }

  &__brand-name {
    font-weight: var(--fw-semibold);
  }

  &__title {
    font-size: var(--fs-body);
    color: var(--ink);
  }

  &__protocol {
    display: inline-flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  &__protocol-name {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--ink);
  }

  &__spacer {
    flex: 1;
  }

  // -----------------------------------------------
  // Breadcrumb mode
  // -----------------------------------------------
  &--breadcrumb {
    height: 52px;
    padding: var(--space-3) var(--space-5);
    justify-content: space-between;
  }

  &__breadcrumb {
    flex: 1;
    min-width: 0;
  }

  &__breadcrumb-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__breadcrumb-item {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &--view {
      font-weight: var(--fw-semibold);
    }
  }

  &__breadcrumb-sep {
    font-family: var(--font-mono);
    font-size: var(--fs-mono);
    color: var(--stone);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-shrink: 0;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    height: 36px;
    padding: 0 var(--space-4);
    border-radius: var(--r-3);
    font-family: var(--font-ui);
    font-size: var(--fs-body);
    font-weight: var(--fw-medium);
    cursor: pointer;
    border: 1px solid transparent;
    transition:
      background-color var(--d-fast) var(--ease-out),
      color var(--d-fast) var(--ease-out);

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }

    &--secondary {
      background: transparent;
      color: var(--ink);
      border-color: var(--mist);

      &:hover:not(:disabled) {
        background: var(--paper-3);
      }

      &.kds-topbar__cta--disabled,
      &:disabled {
        color: var(--stone-2);
        cursor: not-allowed;
        opacity: 1;
      }
    }

    &--primary {
      background: var(--ink);
      color: var(--paper);
      border-color: transparent;

      &:hover:not(:disabled) {
        background: var(--ink-3);
      }

      &:active:not(:disabled) {
        background: var(--ink-2);
      }

      &.kds-topbar__cta--disabled,
      &:disabled {
        background: var(--mist);
        color: var(--stone-2);
        cursor: not-allowed;
        opacity: 1;
      }
    }
  }
}
</style>
